<template>
  <Navbar>
    <template #brand>
      <div class="brand-wrapper">
        <img :src="logo" alt="Accelerate Advisory" class="brand-logo" />
        <span class="brand-text">Accelerate Advisory</span>
      </div>
    </template>
    <template #menu>
      <NavLink :href="linkHome">Home</NavLink>
      <NavLink :href="linkAbout">About</NavLink>
      <NavLink :href="linkCalculator">Calculator</NavLink>
      <NavLink :href="linkContact">Contact</NavLink>
    </template>
  </Navbar>
  <div class="header-section">
    <h1>Loan Comparison Calculator</h1>
  </div>
  <main class="main-content">
    <div class="instructions-section">
      <div class="instructions-content">
        <p class="instructions-summary">
          <strong>Compare loan options:</strong> Enter details in Option 1, add Option 2 (or more tabs), then view the comparison summary below. 
          <span class="instructions-tip">Multiple loans per option are supported with automatic balance flow.</span>
        </p>
      </div>
    </div>
    <div class="tabs">
      <div class="tab-headers">
        <span v-for="(tab, idx) in tabs" :key="tab.id" class="tab-wrapper">
          <button :class="{ active: idx === activeTab }" @click="activeTab = idx" class="tab-btn-with-close">
            <span class="tab-label">Option {{ idx + 1 }}</span>
            <span v-if="tabs.length > 1" class="tab-close-inline" @click.stop="removeTab(idx)"
              :aria-label="`Remove Option ${idx + 1}`" title="Remove option">&times;</span>
          </button>
        </span>
        <button @click="addTab">+ Add Tab</button>
      </div>
      <div class="tab-content">
        <Calculator v-for="(tab, idx) in tabs" v-show="idx === activeTab" :key="tab.id"
          @summary="updateSummary(idx, $event)" />
      </div>
      <div class="loan-comparison">
        <h2>Comparison Summary</h2>
        <div v-if="tabs.length > 1">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Principal Paid (Total)</th>
                <th>Interest Paid (Total)</th>
                <th>Remaining</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(summary, idx) in summaries" :key="tabs[idx].id">
                <td data-label="Option">Option {{ idx + 1 }}</td>
                <td data-label="Principal Paid">
                  <span v-if="summary && summary.length">
                    {{summary.reduce((sum, loan) => sum + (loan.principalPaid || 0), 0).toLocaleString('en-US', {
                      style:
                        'currency', currency: 'USD'
                    })}}
                  </span>
                  <span v-else>—</span>
                </td>
                <td data-label="Interest Paid">
                  <span v-if="summary && summary.length">
                    {{summary.reduce((sum, loan) => sum + (loan.interestPaid || 0), 0).toLocaleString('en-US', {
                      style:
                        'currency', currency: 'USD'
                    })}}
                  </span>
                  <span v-else>—</span>
                </td>
                <td data-label="Remaining">
                  <span v-if="summary && summary.length && summary[summary.length - 1]">
                    {{ summary[summary.length - 1].remaining != null
                      ? summary[summary.length - 1].remaining.toLocaleString('en-US', {
                        style: 'currency', currency: 'USD'
                      })
                      : 'N/A' }}
                  </span>
                  <span v-else>No data yet</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <p>Add more tabs to compare different loan options.</p>
        </div>
      </div>
    </div>
  </main>
  <div class="disclaimer">
    <p><strong>Disclaimer:</strong> This calculator is for illustrative purposes only and does not constitute financial
      advice. Results are estimates based on the information provided and may not reflect actual loan terms. Please
      consult with a qualified financial advisor before making any financial decisions.</p>
  </div>
  <Footer brand="Accelerate Advisory" :description="footerDescription" :email="decodedEmail" :phone="decodedPhone"
    address="Mount Wellington, Auckland, New Zealand">
    <template #links>
      <a :href="linkAbout" class="footer__link">About Us</a>
      <a :href="linkCalculator" class="footer__link">Calculator</a>
      <a :href="linkContact" class="footer__link">Contact</a>
    </template>
  </Footer>
</template>

<script>
import { ref } from 'vue'
import { Navbar, NavLink, Footer } from '@accelerateadvisory/frontend-core'
import Calculator from './components/Calculator.vue'
import logo from './assets/logos/logo.png'

export default {
  name: 'App',
  components: {
    Navbar,
    NavLink,
    Footer,
    Calculator
  },
  setup() {
    const tabs = ref([
      { id: 1 },
      { id: 2 }
    ])
    const activeTab = ref(0)
    let nextId = 3
    const summaries = ref(tabs.value.map(() => null))

    // Get encoded contact details from environment variables
    const encodedEmail = process.env.VUE_APP_CONTACT_EMAIL || ''
    const encodedPhone = process.env.VUE_APP_CONTACT_PHONE || ''
    const linkHome = process.env.VUE_APP_LINK_HOME || '#'
    const linkAbout = linkHome !== '#' ? `${linkHome.replace(/\/$/, '')}/about` : '#'
    const linkContact = linkHome !== '#' ? `${linkHome.replace(/\/$/, '')}/contact` : '#'
    const linkCalculator = process.env.VUE_APP_LINK_CALCULATOR || '#'

    const footerDescription = "Chartered accountants dedicated to helping property investors and business owners maximise returns and minimise tax. We offer growth-focused accounting, smart tax planning, so you can build wealth with clarity and confidence."

    const decodedEmail = ref('')
    const decodedPhone = ref('')

    // Decode after component mounts
    if (typeof window !== 'undefined' && encodedEmail && encodedPhone) {
      try {
        decodedEmail.value = atob(encodedEmail)
        decodedPhone.value = atob(encodedPhone)
      } catch (e) {
        console.error('Failed to decode contact details')
      }
    }

    function addTab() {
      tabs.value.push({ id: nextId++ })
      summaries.value.push(null)
      activeTab.value = tabs.value.length - 1
    }
    function removeTab(idx) {
      tabs.value.splice(idx, 1)
      summaries.value.splice(idx, 1)
      if (activeTab.value >= tabs.value.length) {
        activeTab.value = tabs.value.length - 1
      }
    }
    function updateSummary(idx, summary) {
      summaries.value[idx] = summary;
    }
    return {
      tabs,
      activeTab,
      addTab,
      removeTab,
      summaries,
      updateSummary,
      decodedEmail,
      decodedPhone,
      footerDescription,
      logo,
      linkHome,
      linkAbout,
      linkContact,
      linkCalculator
    }
  }
}
</script>

<style>
#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--color-text);
  margin-top: 0;
  background: var(--color-bg);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  width: 100%;
}

.header-section {
  background-color: var(--color-primary);
  color: #fff;
  padding: 60px 20px;
  text-align: center;
}

h1 {
  margin: 0;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  font-size: clamp(1.5rem, 5vw, 3rem);
  font-weight: 800;
  color: inherit;
}

.instructions-section {
  background: var(--color-primary-light);
  margin: 0 auto;
  max-width: 1200px;
  margin-top: var(--space-lg);
  margin-bottom: var(--space-md);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(23, 54, 65, 0.1);
}

.instructions-content {
  padding: var(--space-md) var(--space-lg);
}

.instructions-summary {
  color: var(--color-primary);
  font-size: 0.95rem;
  line-height: 1.4;
  text-align: center;
  margin: 0;
  font-weight: 500;
}

.instructions-summary strong {
  font-weight: 600;
}

.instructions-tip {
  color: rgba(23, 54, 65, 0.8);
  font-size: 0.85rem;
  font-style: italic;
  display: block;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .instructions-section {
    margin-left: var(--space-md);
    margin-right: var(--space-md);
  }

  .instructions-content {
    padding: var(--space-sm) var(--space-md);
  }

  .instructions-summary {
    font-size: 0.9rem;
    text-align: left;
  }

  .instructions-tip {
    font-size: 0.8rem;
  }
}

.tabs {
  margin: var(--space-xl) auto;
  max-width: 1200px;
  padding: 0 var(--space-md);
}

.loan-comparison {
  max-width: 1200px;
  margin: var(--space-xl) auto;
  padding: 0;
}

.tab-headers {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.tab-headers button {
  margin: 0;
  padding: 0.75rem 1.25rem;
  border: 2px solid transparent;
  background: var(--color-bg-white);
  color: var(--color-primary);
  cursor: pointer;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all var(--transition-smooth);
  box-shadow: var(--shadow-sm);
}

.tab-headers button:hover:not(.active) {
  background: var(--color-bg-light);
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.tab-btn-with-close {
  padding-right: 0.75rem;
}

.tab-btn-with-close .tab-close-inline {
  margin-left: var(--space-sm);
  font-size: 1rem;
  padding: 2px 6px;
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
}

.tab-headers button.active {
  background: var(--color-primary-light);
  color: var(--color-primary);
  border-color: transparent;
  box-shadow: var(--shadow-primary);
}

.tab-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.tab-label {
  display: inline-block;
}

.tab-close-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: var(--space-sm);
  font-size: 0.85rem;
  line-height: 1;
  color: rgba(23, 54, 65, 0.6);
  cursor: pointer;
  padding: 3px 6px;
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
}

.tab-close-inline:hover {
  background: rgba(23, 54, 65, 0.1);
  color: var(--color-primary);
  transform: scale(1.1);
}

.tab-close-inline:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.5);
}

.tab-content {
  border: none;
  border-radius: 0 var(--radius-lg) var(--radius-lg) var(--radius-lg);
  padding: var(--space-xl);
  background: var(--color-bg-white);
  box-shadow: var(--shadow-md);
}

.loan-comparison {
  background: var(--color-bg-white);
  border-radius: var(--radius-xl);
  padding: var(--space-xl);
  box-shadow: var(--shadow-md);
}

.loan-comparison h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: var(--space-lg);
  text-align: left;
}

.comparison-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: var(--space-md);
  font-size: 0.9rem;
  overflow: hidden;
  border-radius: var(--radius-lg);
}

.comparison-table th,
.comparison-table td {
  padding: var(--space-md) var(--space-lg);
  text-align: left;
  width: 25%;
}

.comparison-table th {
  background: var(--color-primary-light);
  color: var(--color-primary);
  white-space: nowrap;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
}

.comparison-table tbody tr {
  transition: all var(--transition-fast);
}

.comparison-table tbody tr:nth-child(even) {
  background: var(--color-bg-light);
}

.comparison-table tbody tr:hover {
  background: var(--color-bg-hover);
  transform: scale(1.01);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .loan-comparison {
    padding: var(--space-md);
  }

  .comparison-table {
    font-size: 0.8rem;
  }

  .comparison-table th,
  .comparison-table td {
    padding: 0.5rem 0.4rem;
    font-size: 0.75rem;
    width: auto;
  }

  .comparison-table th {
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {

  .comparison-table,
  .comparison-table thead,
  .comparison-table tbody,
  .comparison-table th,
  .comparison-table td,
  .comparison-table tr {
    display: block;
  }

  .comparison-table thead {
    display: none;
  }

  .comparison-table tbody tr {
    margin-bottom: var(--space-md);
    border: none;
    border-radius: var(--radius-lg);
    background: var(--color-primary-light);
    padding: var(--space-md);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .comparison-table tbody tr:nth-child(even) {
    background: var(--color-primary-light);
  }

  .comparison-table tbody tr:hover {
    transform: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .comparison-table td {
    border: none;
    padding: var(--space-sm) 0;
    text-align: left;
    display: flex;
    justify-content: space-between;
    gap: var(--space-sm);
    color: var(--color-primary);
  }

  .comparison-table td:first-child {
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: var(--space-sm);
    border-bottom: 2px solid rgba(23, 54, 65, 0.2);
    padding-bottom: 0.75rem;
    justify-content: flex-start;
  }

  .comparison-table td:before {
    content: attr(data-label);
    font-weight: 600;
    color: var(--color-primary);
  }

  .comparison-table td:first-child:before {
    content: "";
  }
}

.brand-wrapper {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.brand-logo {
  height: 32px;
  width: auto;
}

.brand-text {
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--color-primary);
}

.disclaimer {
  max-width: 1200px;
  margin: 0 auto var(--space-md);
  padding: 0 var(--space-md);
  font-size: 0.75rem;
  color: var(--color-text);
  opacity: 0.6;
  text-align: center;
  line-height: 1.5;
}

/* Override footer link colors */
.footer .footer__link {
  color: var(--color-white) !important;
}

.footer .footer__link:hover {
  opacity: 0.8;
}

@media (max-width: 450px) {
  .brand-text {
    font-size: 1rem;
  }
}
</style>
